import React, { useEffect, useState } from 'react';

import { StateLanguage } from '../../languages/config/StateLanguage';
import { MediaDA } from '../../services/admin/media.service';
import { authCompany } from '../../scripts/authentication.script';
import { usePdfViewer } from '../../contexts/PdfViewerContext';
import { offcanvasShow, offcanvasHide } from '../../utils/offcanvas.util';
import { attachmentPdfFilename } from '../../scripts/document.filename.script';

import AppPdfViewer from '../element/PdfViewer';
import { HEADER_HEIGHT } from '../../styles/layout';

const PANEL_WIDTH = 500

interface Props {
  isPush?: boolean;
}

const PdfViewerPushPanel: React.FC<Props> = ({isPush}) => {
  const {lang} = StateLanguage()
  const {activeDocumentId, directPdfUrl, downloadFilename, isOpen, isPushModePdf, close} = usePdfViewer()
  const effectivePush = isPush ?? isPushModePdf
  const [signedUrl, setSignedUrl] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)

  // Load signed URL when document changes (document ID mode)
  useEffect(() => {
    if (!activeDocumentId || !isOpen) {
      setSignedUrl(null)
      return
    }

    const company = authCompany()
    if (!company?.id) return

    setLoading(true)
    setSignedUrl(null)

    MediaDA.getDocumentSignedUrl(company.id, activeDocumentId)
      .then((url) => {
        setSignedUrl(url)
      })
      .finally(() => {
        setLoading(false)
      })
  }, [activeDocumentId, isOpen])

  // Manage overlay classes when switching between push and overlay while open
  useEffect(() => {
    if (isOpen && effectivePush) {
      offcanvasHide('btn-pdf-viewer', 'drawer-pdf-viewer', 'overlay-pdf-viewer')
    } else if (isOpen && !effectivePush) {
      offcanvasShow('btn-pdf-viewer', 'drawer-pdf-viewer', 'overlay-pdf-viewer')
    }
  }, [effectivePush, isOpen])

  const executeClose = () => {
    offcanvasHide('btn-pdf-viewer', 'drawer-pdf-viewer', 'overlay-pdf-viewer')
    close()
  }

  const executeDownload = () => {
    const url = directPdfUrl || signedUrl
    if (!url) return

    const link = document.createElement('a')
    link.href = url
    link.download = attachmentPdfFilename(downloadFilename, 'document')
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  if (!isOpen) return null

  const displayUrl = directPdfUrl || signedUrl
  const showDownload = !!displayUrl

  const panelContent = (
    <div className="card shadow-none rounded-0 w-100 h-100 d-flex flex-column">
      <div className="card-header py-3 d-flex align-items-center justify-content-between" style={{minHeight: '50px'}}>
        <h4 className="card-title m-0 fs-6">{lang.labels.referencePdf}</h4>
        <div className="d-flex align-items-center">
          {showDownload && (
            <button
              type="button"
              className="btn btn-sm btn-light-primary me-2"
              title={lang.labels.download}
              onClick={executeDownload}
            >
              <i className="bi bi-download fs-5"></i>
            </button>
          )}
          <button
            type="button"
            className="btn btn-sm btn-icon btn-active-light-danger"
            onClick={executeClose}
          >
            <span className="svg-icon svg-icon-1">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                <rect opacity="0.5" x="6" y="17.3137" width="16" height="2" rx="1" transform="rotate(-45 6 17.3137)" fill="currentColor"/>
                <rect x="7.41422" y="6" width="16" height="2" rx="1" transform="rotate(45 7.41422 6)" fill="currentColor"/>
              </svg>
            </span>
          </button>
        </div>
      </div>
      <div className="card-body p-3 d-flex flex-column flex-grow-1" style={{overflow: 'hidden'}}>
        {loading && (
          <div className="d-flex align-items-center justify-content-center flex-grow-1">
            <span className="spinner-border spinner-border-sm text-primary me-2"></span>
            <span className="text-muted fs-7">{lang.labels.loading}...</span>
          </div>
        )}
        {!loading && displayUrl && (
          <div style={{flex: 1, minHeight: 0}}>
            <AppPdfViewer pdf={displayUrl} />
          </div>
        )}
        {!loading && !displayUrl && (activeDocumentId || directPdfUrl) && (
          <div className="d-flex align-items-center justify-content-center flex-grow-1">
            <span className="text-muted fs-7">{lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain}</span>
          </div>
        )}
      </div>
    </div>
  )

  // Push mode — inline sticky panel
  if (effectivePush) {
    return (
      <div
        className="d-flex flex-column border-start bg-white"
        style={{
          width: `${PANEL_WIDTH}px`,
          flex: `0 0 ${PANEL_WIDTH}px`,
          position: 'sticky',
          top: `${HEADER_HEIGHT}px`,
          height: `calc(100vh - ${HEADER_HEIGHT}px)`,
          transition: 'width 0.3s ease, flex 0.3s ease',
        }}
      >
        {panelContent}
      </div>
    )
  }

  // Overlay mode — drawer end
  return (
    <>
      <div
        id="drawer-pdf-viewer"
        className="bg-white drawer drawer-end"
        style={{width: `${PANEL_WIDTH}px`}}
        data-kt-drawer="true"
      >
        {panelContent}
      </div>
      <div id="overlay-pdf-viewer" onClick={executeClose}></div>
    </>
  )
}

export default PdfViewerPushPanel;
